/**
 * Copyright 2025 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { useCallback } from "react";
import {
  ResourceExecutionStatus,
  useGetGlobalEventActionResourceStateLazyQuery,
} from "~/generated/graphql";

type InitializeActionCounts = (
  eventActionId: string,
  resourceStates: Array<{ id: string; status: ResourceExecutionStatus }>
) => void;

/**
 * Hook to load and initialize resource states for an event action.
 * Handles pagination automatically and initializes counts via the provided callback.
 */
export function useLoadResourceStatesForAction(
  eventId: string,
  initializeActionCounts: InitializeActionCounts
) {
  const [fetchResourceStates] = useGetGlobalEventActionResourceStateLazyQuery();

  return useCallback(
    async (actionId: string): Promise<void> => {
      if (!actionId) {
        return;
      }

      const PAGE_SIZE = 200;

      const fetchPage = async (
        pageNumber: number,
        accumulated: ReadonlyArray<{
          id: string;
          status: ResourceExecutionStatus;
        }>,
        expectedCount?: number
      ): Promise<
        ReadonlyArray<{ id: string; status: ResourceExecutionStatus }>
      > => {
        const response = await fetchResourceStates({
          variables: {
            eventId,
            eventActionId: actionId,
            page: pageNumber,
            limit: PAGE_SIZE,
            excludeGroupStatus: true,
          },
          fetchPolicy: "network-only",
        });

        const pageData = response.data?.globalEventActionResourceState;
        if (!pageData) {
          return accumulated;
        }

        const pageStates =
          pageData.result
            ?.flatMap((group) => group.resourceStates ?? [])
            .filter((resourceState) =>
              Boolean(resourceState?.id && resourceState?.status)
            )
            .map((resourceState) => ({
              id: resourceState.id,
              status: resourceState.status as ResourceExecutionStatus,
            })) ?? [];

        const combined = [...accumulated, ...pageStates];
        const totalCount = expectedCount ?? pageData.pageInfo?.count ?? 0;

        if (
          totalCount === 0 ||
          combined.length >= totalCount ||
          pageStates.length === 0
        ) {
          return combined;
        }

        return fetchPage(pageNumber + 1, combined, totalCount);
      };

      const aggregated = await fetchPage(1, []);
      initializeActionCounts(actionId, [...aggregated]);
    },
    [eventId, fetchResourceStates, initializeActionCounts]
  );
}
